import { useCallback, useRef, useState, type ReactNode } from 'react';
import { ConfirmContext, type ConfirmFn, type ConfirmOptions } from '../../lib/confirm';
import { Modal } from './Modal';

interface PendingConfirm extends ConfirmOptions {
  resolve: (confirmed: boolean) => void;
}

/** Provides `useConfirm()` and renders at most one confirmation dialog at a time. */
export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [pending, setPending] = useState<PendingConfirm | null>(null);
  const pendingRef = useRef<PendingConfirm | null>(null);

  const confirm = useCallback<ConfirmFn>(
    (options) =>
      new Promise<boolean>((resolve) => {
        // A newer request supersedes an unanswered one.
        pendingRef.current?.resolve(false);
        const next = { ...options, resolve };
        pendingRef.current = next;
        setPending(next);
      }),
    [],
  );

  const settle = (confirmed: boolean) => {
    const current = pendingRef.current;
    pendingRef.current = null;
    setPending(null);
    current?.resolve(confirmed);
  };

  return (
    <ConfirmContext value={confirm}>
      {children}
      {pending ? (
        <Modal title={pending.title} onClose={() => settle(false)}>
          {pending.message ? (
            <div className="modal-body">
              <p className="modal-message">{pending.message}</p>
            </div>
          ) : null}
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={() => settle(false)}>
              {pending.cancelLabel ?? 'Abbrechen'}
            </button>
            <button
              type="button"
              className={`btn ${pending.tone === 'danger' ? 'btn-danger' : 'btn-primary'}`}
              onClick={() => settle(true)}
              autoFocus
            >
              {pending.confirmLabel ?? 'Bestätigen'}
            </button>
          </div>
        </Modal>
      ) : null}
    </ConfirmContext>
  );
}
